import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, AlertCircleIcon, LockIcon } from 'lucide-react';
import { CodeInput } from '../components/CodeInput';
import { SKRResultCard } from '../components/SKRResultCard';
import { findByCode, type SKRRecord } from '../lib/skrStore';
type Status = 'idle' | 'notfound' | 'found';
export function VerifySKR() {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [record, setRecord] = useState<SKRRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (code.length < 7) {
      setError('Please enter your full SKR code as printed on your receipt.');
      return;
    }
    const found = findByCode(code);
    if (found) {
      setRecord(found);
      setStatus('found');
    } else {
      setRecord(null);
      setStatus('notfound');
    }
  };
  const reset = () => {
    setCode('');
    setStatus('idle');
    setRecord(null);
    setError(null);
  };
  return <div className="w-full bg-steca-50/40" data-id="element-341" data-mp="2tn27b52h0lj7">
      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20" data-id="element-342" data-mp="2tl7mdqr78tf4">
        <div className="text-center" data-id="element-343" data-mp="2op0o8k51om7q">
          <span className="inline-flex items-center gap-2 rounded-full bg-steca-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-steca-700" data-id="element-344" data-mp="2olv4lkf6qcje">
            <LockIcon className="h-3.5 w-3.5" data-id="element-345" data-mp="3hblknz0dnoi5" />
            Secure Verification
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-steca-ink sm:text-4xl" data-id="element-346" data-mp="3cf3f4wx83m4r">
            Confirm Your Safe Keeping Receipt
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-steca-ink/70" data-id="element-347" data-mp="33ddp9vizov93">
            Enter the code from your Safe Keeping Receipt (SKR) to view the verified details of the
            assets held in your name at Steca Global Security.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-md rounded-2xl border border-steca-100 bg-white p-6 shadow-sm sm:p-8" data-id="element-348" data-mp="1rwdqch24zi63">
          <label className="mb-3 block text-center text-sm font-medium text-steca-ink/70" data-id="element-349" data-mp="1yegqohi76ouh">
            Enter your SKR code
          </label>
          <CodeInput value={code} onChange={v => {
          setCode(v);
          setError(null);
        }} data-id="element-350" data-mp="1kgigzpetnglb" />
          {error && <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-red-600" data-id="element-351" data-mp="1ohvo02tnhptt">
              <AlertCircleIcon className="h-4 w-4" data-id="element-352" data-mp="1amjkp71fir1a" />
              {error}
            </p>}
          <button type="submit" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-steca-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-steca-700" data-id="element-353" data-mp="1fh5ux6vrydjs">
            <SearchIcon className="h-4 w-4" data-id="element-354" data-mp="rp8lfmfsrxlu" />
            Verify SKR
          </button>
          {status !== 'idle' && <button type="button" onClick={reset} className="mt-3 w-full text-center text-sm font-medium text-steca-600 hover:text-steca-700" data-id="element-355" data-mp="wnjsxqimdw0w">
              Clear &amp; verify another code
            </button>}
        </form>
        <AnimatePresence mode="wait" data-id="element-356" data-mp="m4suw61615qq">
          {status === 'found' && record && <motion.div key="found" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="mt-8" data-id="element-357" data-mp="i1mp0zhytgks">
              <SKRResultCard record={record} data-id="element-358" data-mp="r3m6tu0t0oos" />
            </motion.div>}
          {status === 'notfound' && <motion.div key="notfound" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="mx-auto mt-8 max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center" data-id="element-359" data-mp="15r7d1wsz740x">
              <AlertCircleIcon className="mx-auto h-8 w-8 text-red-500" data-id="element-360" data-mp="19uwzafm6h9is" />
              <h2 className="mt-2 font-display text-lg font-bold text-red-700" data-id="element-361" data-mp="348hjotq8z2x7">No record found</h2>
              <p className="mt-1 text-sm text-red-600/90" data-id="element-362" data-mp="397lqxetm2qkw">
                We couldn&apos;t find an SKR matching that code. Please check the code on your receipt
                and try again, or contact our vault team for assistance.
              </p>
            </motion.div>}
        </AnimatePresence>
      </section>
    </div>;
}